import { useState, useCallback } from 'react';
import { TrendingUp, Loader2, RefreshCw, ChevronDown, ChevronRight, ArrowUpCircle, ArrowDownCircle, MinusCircle, Eye } from 'lucide-react';
import BucketGrid, { bucketKey } from '../components/pricingIntelligence/BucketGrid';
import RecommendationDetail from '../components/pricingIntelligence/RecommendationDetail';
import { generatePricingSheet } from '../services/pricingIntelligenceApi';
import type { PricingSheet, PricingHorizon, DepotRegion, VehicleType, ManagerBriefSection, BriefCategory } from '../types/pricingIntelligence';
import { ALL_REGIONS, ALL_VEHICLE_CLASSES } from '../types/pricingIntelligence';

type PageState = 'idle' | 'loading' | 'success' | 'error';

const HORIZON_LABELS: Record<PricingHorizon, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Small helpers ──────────────────────────────────────────────────────────

function ToggleChip<T extends string>({
  label,
  value,
  selected,
  onChange,
}: {
  label: string;
  value: T;
  selected: boolean;
  onChange: (value: T, checked: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(value, !selected)}
      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
        selected
          ? 'bg-success/50 border-primary/60 text-success'
          : 'bg-card border-border text-muted-foreground hover:border-accent'
      }`}
    >
      {label}
    </button>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{children}</h2>
  );
}

// ── Brief section card ─────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<BriefCategory, { icon: typeof ArrowUpCircle; color: string; bg: string; border: string }> = {
  increase: { icon: ArrowUpCircle,   color: 'text-success',  bg: 'bg-success/20',  border: 'border-primary/40' },
  decrease: { icon: ArrowDownCircle, color: 'text-destructive',    bg: 'bg-destructive/20',    border: 'border-destructive/40' },
  hold:     { icon: MinusCircle,     color: 'text-muted-foreground',   bg: 'bg-background/20',   border: 'border-border/40' },
  monitor:  { icon: Eye,             color: 'text-warning',  bg: 'bg-warning/20',  border: 'border-warning/40' },
};

function BriefSectionCard({
  section,
  category,
  onSelectBucket,
}: {
  section: ManagerBriefSection;
  category: BriefCategory;
  onSelectBucket: (key: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const cfg = CATEGORY_CONFIG[category];
  const Icon = cfg.icon;

  if (section.items.length === 0) return null;

  return (
    <div className={`${cfg.bg} rounded-xl border ${cfg.border} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
      >
        <Icon className={`w-4 h-4 ${cfg.color}`} />
        <span className={`text-sm font-medium ${cfg.color}`}>{section.title}</span>
        <span className="text-xs text-muted-foreground ml-1">({section.items.length})</span>
        <span className="ml-auto">
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
            : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {section.items.map((item, i) => (
            <button
              key={i}
              onClick={() => onSelectBucket(bucketKey(item.region, item.vehicleClass))}
              className="w-full text-left bg-background/40 hover:bg-background/70 rounded-lg px-3 py-2 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground font-medium">{item.headline}</span>
                <span className="text-xs text-muted-foreground font-mono">{item.adjustmentRange}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{item.whyNow}</p>
              <p className="text-xs text-muted-foreground mt-0.5 italic">{item.whatThisMeans}</p>
              {item.reviewRequired && (
                <span className="inline-block mt-1 text-xs px-1.5 py-0.5 rounded bg-warning/40 text-warning border border-warning/30">
                  Needs review
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function PricingIntelligencePage() {
  const [horizon, setHorizon] = useState<PricingHorizon>('weekly');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(today());
  const [regions, setRegions] = useState<DepotRegion[]>([...ALL_REGIONS]);
  const [vehicleClasses, setVehicleClasses] = useState<VehicleType[]>([...ALL_VEHICLE_CLASSES]);

  const [pageState, setPageState] = useState<PageState>('idle');
  const [sheet, setSheet] = useState<PricingSheet | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const toggleRegion = useCallback((r: DepotRegion, checked: boolean) => {
    setRegions(prev =>
      checked ? [...prev, r] : prev.filter(x => x !== r)
    );
  }, []);

  const toggleVehicleClass = useCallback((vc: VehicleType, checked: boolean) => {
    setVehicleClasses(prev =>
      checked ? [...prev, vc] : prev.filter(x => x !== vc)
    );
  }, []);

  const handleGenerate = useCallback(async () => {
    if (regions.length === 0 || vehicleClasses.length === 0) return;
    setPageState('loading');
    setErrorMsg('');
    setSelectedKey(null);
    try {
      const result = await generatePricingSheet({
        horizon,
        effectiveFrom,
        regions,
        vehicleClasses,
      });
      setSheet(result);
      setPageState('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'An unexpected error occurred');
      setPageState('error');
    }
  }, [horizon, effectiveFrom, regions, vehicleClasses]);

  const selectedRec = sheet?.recommendations.find(r =>
    bucketKey(r.regionId, r.vehicleClass) === selectedKey
  ) ?? null;

  const isLoading = pageState === 'loading';

  return (
    <div className="space-y-5">
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-success" />
          <h1 className="text-xl font-semibold text-foreground">Pricing Adjustment Brief</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">
          Where to push prices, where to discount, and what to watch.
        </p>
      </div>

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <div className="bg-card/40 rounded-xl border border-border/50 p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Horizon */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Horizon</label>
            <select
              value={horizon}
              onChange={e => setHorizon(e.target.value as PricingHorizon)}
              disabled={isLoading}
              className="w-full bg-secondary/50 border border-accent rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-success disabled:opacity-50"
            >
              {(Object.entries(HORIZON_LABELS) as Array<[PricingHorizon, string]>).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* Effective from */}
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Effective From</label>
            <input
              type="date"
              value={effectiveFrom}
              onChange={e => setEffectiveFrom(e.target.value)}
              disabled={isLoading}
              className="w-full bg-secondary/50 border border-accent rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-success disabled:opacity-50"
            />
          </div>
        </div>

        {/* Regions */}
        <div>
          <label className="block text-xs text-muted-foreground mb-1.5">Regions</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_REGIONS.map(r => (
              <ToggleChip
                key={r}
                label={r}
                value={r}
                selected={regions.includes(r)}
                onChange={toggleRegion}
              />
            ))}
          </div>
        </div>

        {/* Vehicle classes */}
        <div>
          <label className="block text-xs text-muted-foreground mb-1.5">Vehicle Classes</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_VEHICLE_CLASSES.map(vc => (
              <ToggleChip
                key={vc}
                label={vc}
                value={vc}
                selected={vehicleClasses.includes(vc)}
                onChange={toggleVehicleClass}
              />
            ))}
          </div>
        </div>

        {/* Generate button */}
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleGenerate}
            disabled={isLoading || regions.length === 0 || vehicleClasses.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-success disabled:bg-secondary disabled:text-muted-foreground text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4" />
            )}
            {isLoading ? 'Generating…' : 'Generate Brief'}
          </button>

          {pageState === 'success' && sheet && (
            <button
              onClick={handleGenerate}
              disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 text-muted-foreground hover:text-foreground text-sm transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Regenerate
            </button>
          )}

          {regions.length === 0 && (
            <span className="text-xs text-warning">Select at least one region</span>
          )}
          {vehicleClasses.length === 0 && (
            <span className="text-xs text-warning">Select at least one vehicle class</span>
          )}
        </div>
      </div>

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {pageState === 'loading' && (
        <div className="flex flex-col items-center justify-center min-h-75 gap-3">
          <Loader2 className="w-8 h-8 text-success animate-spin" />
          <p className="text-sm text-muted-foreground">Analysing truth layer data…</p>
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {pageState === 'error' && (
        <div className="bg-destructive/20 border border-destructive/40 rounded-xl p-4 flex items-start gap-3">
          <span className="text-destructive text-lg leading-none">✕</span>
          <div>
            <p className="text-destructive text-sm font-medium">Failed to generate sheet</p>
            <p className="text-destructive/70 text-xs mt-0.5">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* ── Results ───────────────────────────────────────────────────────── */}
      {pageState === 'success' && sheet && (
        <div className="space-y-4">
          {/* Coverage banner */}
          {sheet.summary.coverage.lowConfidenceSheet && (
            <div className="bg-warning/20 border border-warning/40 rounded-xl px-4 py-3 flex items-start gap-2">
              <Eye className="w-4 h-4 text-warning mt-0.5 shrink-0" />
              <div>
                <p className="text-warning text-sm font-medium">Low confidence brief</p>
                <p className="text-warning/70 text-xs mt-0.5">
                  Only {sheet.summary.coverage.mappedBuckets} of {sheet.summary.coverage.totalBuckets} buckets
                  have mapped regions. Average confidence: {(sheet.summary.coverage.averageConfidence * 100).toFixed(0)}%.
                  Treat these recommendations as directional only.
                </p>
              </div>
            </div>
          )}

          {/* Manager narrative */}
          <div className="bg-card/40 rounded-xl border border-border/50 p-4">
            <div className="flex items-center justify-between mb-2">
              <SectionHeader>Brief</SectionHeader>
              <span className="text-xs text-muted-foreground font-mono">
                {sheet.horizon} · {sheet.effectiveFrom} → {sheet.effectiveTo}
              </span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed">{sheet.summary.brief.narrative}</p>
          </div>

          {/* Brief sections: increase / decrease / hold / monitor */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <BriefSectionCard section={sheet.summary.brief.increase} category="increase" onSelectBucket={setSelectedKey} />
            <BriefSectionCard section={sheet.summary.brief.decrease} category="decrease" onSelectBucket={setSelectedKey} />
            <BriefSectionCard section={sheet.summary.brief.hold}     category="hold"     onSelectBucket={setSelectedKey} />
            <BriefSectionCard section={sheet.summary.brief.monitor}  category="monitor"  onSelectBucket={setSelectedKey} />
          </div>

          {/* Global data notes */}
          {sheet.summary.globalDataNotes && sheet.summary.globalDataNotes.length > 0 && (
            <div className="bg-background/40 rounded-xl border border-border/30 px-4 py-3">
              <p className="text-xs text-muted-foreground font-medium mb-1.5">Data context</p>
              {sheet.summary.globalDataNotes.map((n, i) => (
                <p key={i} className="text-xs text-muted-foreground">{n}</p>
              ))}
            </div>
          )}

          {/* Drill-down: confidence caveats */}
          {sheet.summary.confidenceCaveats.length > 0 && (
            <div className="bg-background/40 rounded-xl border border-border/30 px-4 py-3">
              <p className="text-xs text-muted-foreground font-medium mb-1">Data Notes</p>
              {sheet.summary.confidenceCaveats.map((c, i) => (
                <p key={i} className="text-xs text-muted-foreground">{c}</p>
              ))}
            </div>
          )}

          {/* Drill-down: grid + detail */}
          <details className="group">
            <summary className="cursor-pointer flex items-center gap-2 text-xs text-muted-foreground transition-colors py-2">
              <ChevronRight className="w-3.5 h-3.5 group-open:rotate-90 transition-transform" />
              Signal matrix &amp; bucket detail
            </summary>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-2">
              <div className="xl:col-span-2 bg-card/40 rounded-xl border border-border/50 p-4">
                <SectionHeader>Recommendations ({sheet.recommendations.length} buckets)</SectionHeader>
                <BucketGrid
                  recommendations={sheet.recommendations}
                  selectedKey={selectedKey}
                  onSelect={setSelectedKey}
                />
              </div>
              <div className="bg-card/40 rounded-xl border border-border/50 p-4 overflow-y-auto max-h-150">
                <SectionHeader>Bucket Detail</SectionHeader>
                <RecommendationDetail recommendation={selectedRec} />
              </div>
            </div>
          </details>

          {/* Metadata strip */}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-1 border-t border-border/40">
            <span>{sheet.metadata.ordersAnalysed} orders analysed</span>
            <span>{sheet.metadata.vehicleDaysAnalysed} vehicle-days</span>
            <span>data {sheet.metadata.inputDataRange.from} → {sheet.metadata.inputDataRange.to}</span>
            <span>{(sheet.metadata.generationDurationMs / 1000).toFixed(1)}s generation</span>
          </div>
        </div>
      )}
    </div>
  );
}
